import { inWindow, instant } from '../time.js'
import { metric, type MetricContext, type MetricResponse, type MetricWindow } from './types.js'

/**
 * Local Insights registry L01–L12 — PRD §9 (local metric registry) and fixtures.
 * Local metrics are on-device, Free, and deterministic from local metadata/
 * event history. Deleted items are excluded; archived items are included.
 */

export type LocalMetricId =
  'L01' | 'L02' | 'L03' | 'L04' | 'L05' | 'L06' | 'L07' | 'L08' | 'L09' | 'L10' | 'L11' | 'L12'

export interface LocalAgreementRecord {
  readonly agreement_id: string
  readonly kind: 'draft' | 'completed' | 'other'
  readonly archived?: boolean
  /** Soft-deleted or permanently deleted: excluded from every local metric. */
  readonly deleted?: boolean
  readonly final_artifact_available?: boolean
  readonly page_count_at_completion?: number
  readonly first_created_at?: string
  readonly first_completed_at?: string
  readonly meaningful_activity_at: string
  readonly template_id?: string
  /** Imported history whose original time is unknown; excluded from created trend. */
  readonly original_time_unknown?: boolean
}

export interface LocalTemplateRecord {
  readonly template_id: string
  readonly deleted?: boolean
}

export interface LocalActivityEvent {
  readonly event_type: string
  readonly agreement_id: string
  readonly occurred_at: string
  readonly template_id?: string
}

export interface LocalStorageEstimate {
  readonly origin_bytes?: number
  readonly app_vault_bytes?: number
  /** Adapter-level approximation flag; breakdown is only shown when reliable. */
  readonly approximate: boolean
  readonly reliable: boolean
}

export interface LocalBackupObservation {
  readonly mode: 'manual' | 'automatic'
  readonly last_success_at?: string
  readonly snapshot_revision?: number
  readonly outstanding_local_changes: boolean
  /** Adapter only initiated a download: the outcome is unknown, not success. */
  readonly initiated_only: boolean
}

export interface LocalMetricsInput {
  readonly agreements: readonly LocalAgreementRecord[]
  readonly templates?: readonly LocalTemplateRecord[]
  readonly events?: readonly LocalActivityEvent[]
  readonly storage?: LocalStorageEstimate
  readonly backup?: LocalBackupObservation
}

export interface TemplateUsage {
  readonly template_id: string
  readonly count: number
  readonly last_used_at: string
}

export interface LocalStorageValue {
  readonly origin_bytes: number | null
  readonly app_vault_bytes: number | null
  readonly approximate: boolean
}

export interface LocalBackupValue {
  readonly mode: 'manual' | 'automatic'
  readonly last_success_at: string | null
  readonly snapshot_revision: number | null
  readonly outstanding_local_changes: boolean
}

export interface LocalMetricOptions {
  readonly as_of: string
  readonly timezone: string
  readonly window: MetricWindow
}

export interface LocalMetricBundle {
  readonly L01: MetricResponse<number>
  readonly L02: MetricResponse<number>
  readonly L03: MetricResponse<number>
  readonly L04: MetricResponse<number>
  readonly L05: MetricResponse<readonly TemplateUsage[]>
  readonly L06: MetricResponse<readonly string[]>
  readonly L07: MetricResponse<number>
  readonly L08: MetricResponse<number>
  readonly L09: MetricResponse<number>
  readonly L10: MetricResponse<number>
  readonly L11: MetricResponse<LocalStorageValue>
  readonly L12: MetricResponse<LocalBackupValue>
}

function mergeAgreement(a: LocalAgreementRecord, b: LocalAgreementRecord): LocalAgreementRecord {
  const earliest = (x: string | undefined, y: string | undefined): string | undefined => {
    if (x === undefined) return y
    if (y === undefined) return x
    return instant(x) <= instant(y) ? x : y
  }
  let merged: LocalAgreementRecord = {
    ...a,
    kind: a.kind !== 'other' ? a.kind : b.kind,
    deleted: a.deleted === true || b.deleted === true,
    archived: a.archived === true || b.archived === true,
    final_artifact_available:
      a.final_artifact_available === true || b.final_artifact_available === true,
    meaningful_activity_at:
      instant(a.meaningful_activity_at) >= instant(b.meaningful_activity_at)
        ? a.meaningful_activity_at
        : b.meaningful_activity_at,
  }
  const created = earliest(a.first_created_at, b.first_created_at)
  if (created !== undefined) merged = { ...merged, first_created_at: created }
  const completed = earliest(a.first_completed_at, b.first_completed_at)
  if (completed !== undefined) merged = { ...merged, first_completed_at: completed }
  const pageCount = a.page_count_at_completion ?? b.page_count_at_completion
  if (pageCount !== undefined) merged = { ...merged, page_count_at_completion: pageCount }
  const templateId = a.template_id ?? b.template_id
  if (templateId !== undefined) merged = { ...merged, template_id: templateId }
  if (a.original_time_unknown === true || b.original_time_unknown === true) {
    merged = { ...merged, original_time_unknown: true }
  }
  return merged
}

/** Stable agreement IDs are the identity: duplicate imports/restores do not add items. */
function dedupeByAgreementId(agreements: readonly LocalAgreementRecord[]): LocalAgreementRecord[] {
  const byId = new Map<string, LocalAgreementRecord>()
  for (const agreement of agreements) {
    const existing = byId.get(agreement.agreement_id)
    byId.set(
      agreement.agreement_id,
      existing === undefined ? agreement : mergeAgreement(existing, agreement),
    )
  }
  return [...byId.values()]
}

function historyStart(agreements: readonly LocalAgreementRecord[]): string | null {
  let earliest: number | null = null
  let earliestIso: string | null = null
  for (const agreement of agreements) {
    for (const candidate of [agreement.first_created_at, agreement.first_completed_at]) {
      if (candidate === undefined) continue
      const ms = instant(candidate)
      if (earliest === null || ms < earliest) {
        earliest = ms
        earliestIso = candidate
      }
    }
  }
  return earliestIso
}

export function computeLocalMetrics(
  input: LocalMetricsInput,
  options: LocalMetricOptions,
): LocalMetricBundle {
  const windowStart = instant(options.window.start)
  const windowEnd = instant(options.window.end)
  const inventory: MetricContext = { as_of: options.as_of, timezone: options.timezone }
  const flow: MetricContext = {
    as_of: options.as_of,
    timezone: options.timezone,
    window: options.window,
  }
  const retainedFrom = historyStart(input.agreements)
  const retained = {
    retained_from: retainedFrom,
    retained_to: options.as_of,
    population: 'this_device_vault',
  }
  const scope = 'local' as const

  const surviving = dedupeByAgreementId(input.agreements.filter((a) => a.deleted !== true))
  const completed = surviving.filter(
    (agreement) => agreement.kind === 'completed' && agreement.final_artifact_available === true,
  )
  const drafts = surviving.filter((agreement) => agreement.kind === 'draft')

  const templates = (input.templates ?? []).filter((template) => template.deleted !== true)
  const templateIds = new Set(templates.map((template) => template.template_id))

  const usageByTemplate = new Map<string, { agreementIds: Set<string>; lastUsedAt: string }>()
  for (const event of input.events ?? []) {
    if (event.event_type !== 'template_instantiated' || event.template_id === undefined) continue
    if (!inWindow(instant(event.occurred_at), windowStart, windowEnd)) continue
    if (!templateIds.has(event.template_id)) continue
    const current = usageByTemplate.get(event.template_id)
    if (current === undefined) {
      usageByTemplate.set(event.template_id, {
        agreementIds: new Set([event.agreement_id]),
        lastUsedAt: event.occurred_at,
      })
    } else {
      current.agreementIds.add(event.agreement_id)
      if (instant(event.occurred_at) > instant(current.lastUsedAt)) {
        current.lastUsedAt = event.occurred_at
      }
    }
  }
  const topTemplates: TemplateUsage[] = [...usageByTemplate.entries()]
    .map(([templateId, usage]) => ({
      template_id: templateId,
      count: usage.agreementIds.size,
      last_used_at: usage.lastUsedAt,
    }))
    .sort((a, b) =>
      b.count !== a.count
        ? b.count - a.count
        : a.last_used_at !== b.last_used_at
          ? instant(b.last_used_at) - instant(a.last_used_at)
          : a.template_id.localeCompare(b.template_id),
    )

  const recent = [...surviving]
    .sort((a, b) =>
      a.meaningful_activity_at !== b.meaningful_activity_at
        ? instant(b.meaningful_activity_at) - instant(a.meaningful_activity_at)
        : a.agreement_id.localeCompare(b.agreement_id),
    )
    .slice(0, 3)
    .map((agreement) => agreement.agreement_id)

  const createdInWindow = surviving.filter(
    (agreement) =>
      agreement.first_created_at !== undefined &&
      inWindow(instant(agreement.first_created_at), windowStart, windowEnd),
  )
  const unknownHistoryCount = surviving.filter(
    (agreement) =>
      agreement.first_created_at === undefined || agreement.original_time_unknown === true,
  ).length

  const completedInWindow = surviving.filter(
    (agreement) =>
      agreement.first_completed_at !== undefined &&
      inWindow(instant(agreement.first_completed_at), windowStart, windowEnd),
  )
  const missingPageCounts = completedInWindow.filter(
    (agreement) => agreement.page_count_at_completion === undefined,
  ).length
  const pagesProcessed = completedInWindow.reduce(
    (sum, agreement) => sum + (agreement.page_count_at_completion ?? 0),
    0,
  )

  const storage = input.storage
  const storageValue: LocalStorageValue | null =
    storage !== undefined && storage.reliable
      ? {
          origin_bytes: storage.origin_bytes ?? null,
          app_vault_bytes: storage.app_vault_bytes ?? null,
          approximate: storage.approximate,
        }
      : null

  const backup = input.backup
  const backupValue: LocalBackupValue | null =
    backup === undefined || backup.initiated_only
      ? null
      : {
          mode: backup.mode,
          last_success_at: backup.last_success_at ?? null,
          snapshot_revision: backup.snapshot_revision ?? null,
          outstanding_local_changes: backup.outstanding_local_changes,
        }

  const flowNotes: string[] = []
  if (unknownHistoryCount > 0) {
    flowNotes.push(`${unknownHistoryCount} imported items have unknown original history`)
  }
  if (missingPageCounts > 0) {
    flowNotes.push(`${missingPageCounts} completions in window lack a page count`)
  }

  return {
    L01: metric({
      metric_id: 'L01',
      value: surviving.length,
      unit: 'count',
      scope,
      context: inventory,
      drilldown_filter: { status: 'surviving' },
      ...retained,
    }),
    L02: metric({
      metric_id: 'L02',
      value: completed.length,
      unit: 'count',
      scope,
      context: inventory,
      drilldown_filter: { status: 'completed_with_local_artifact' },
      ...retained,
    }),
    L03: metric({
      metric_id: 'L03',
      value: drafts.length,
      unit: 'count',
      scope,
      context: inventory,
      drilldown_filter: { status: 'draft' },
      ...retained,
    }),
    L04: metric({
      metric_id: 'L04',
      value: templateIds.size,
      unit: 'count',
      scope,
      context: inventory,
      drilldown_filter: { status: 'template' },
      ...retained,
    }),
    L05: metric({
      metric_id: 'L05',
      value: topTemplates,
      unit: 'items',
      scope,
      context: flow,
      sample_size: topTemplates.reduce((sum, usage) => sum + usage.count, 0),
      completeness: unknownHistoryCount > 0 ? 'partial' : 'complete',
      drilldown_filter: { event: 'template_instantiated' },
      ...retained,
    }),
    L06: metric({
      metric_id: 'L06',
      value: recent,
      unit: 'items',
      scope,
      context: inventory,
      sample_size: recent.length,
      drilldown_filter: { module: 'recent' },
      ...retained,
    }),
    L07: metric({
      metric_id: 'L07',
      value: createdInWindow.length,
      unit: 'count',
      scope,
      context: flow,
      completeness: unknownHistoryCount > 0 ? 'partial' : 'complete',
      unknown_count: unknownHistoryCount > 0 ? unknownHistoryCount : null,
      notes: flowNotes,
      drilldown_filter: { event: 'first_created', surviving: true },
      ...retained,
    }),
    L08: metric({
      metric_id: 'L08',
      value: completedInWindow.length,
      unit: 'count',
      scope,
      context: flow,
      completeness: missingPageCounts > 0 ? 'partial' : 'complete',
      notes: missingPageCounts > 0 ? [`${missingPageCounts} completions lack a page count`] : [],
      drilldown_filter: { event: 'first_completed', surviving: true },
      ...retained,
    }),
    L09: metric({
      metric_id: 'L09',
      value: pagesProcessed,
      unit: 'count',
      scope,
      context: flow,
      completeness: missingPageCounts > 0 ? 'partial' : 'complete',
      notes: missingPageCounts > 0 ? [`${missingPageCounts} completions lack a page count`] : [],
      drilldown_filter: { event: 'first_completed', field: 'page_count_at_completion' },
      ...retained,
    }),
    L10: metric({
      metric_id: 'L10',
      value: pagesProcessed,
      unit: 'estimate',
      scope,
      context: flow,
      completeness: missingPageCounts > 0 ? 'partial' : 'complete',
      notes: ['Estimated pages handled digitally; not measured avoided printing'],
      drilldown_filter: { event: 'first_completed', assumption: 'one_page_equivalent' },
      ...retained,
    }),
    L11: metric({
      metric_id: 'L11',
      value: storageValue,
      unit: 'bytes',
      scope,
      context: inventory,
      completeness: storageValue === null ? 'partial' : 'complete',
      notes: storageValue === null ? ['Storage adapter cannot estimate vault bytes reliably'] : [],
      drilldown_filter: { scope: 'app_vault_and_origin' },
      ...retained,
    }),
    L12: metric({
      metric_id: 'L12',
      value: backupValue,
      unit: 'status',
      scope,
      context: inventory,
      completeness: backupValue === null ? 'insufficient' : 'complete',
      notes:
        backupValue === null
          ? ['Backup outcome unknown: adapter only initiated a download or no observation exists']
          : [],
      drilldown_filter: { source: 'adapter_confirmed_export' },
      ...retained,
    }),
  }
}
